import { ArrowUpTrayIcon, EllipsisHorizontalIcon, PencilSquareIcon, Square2StackIcon, TrashIcon } from '@heroicons/react/24/solid'
import NoImagePlaceholder from 'assets/no-image-placeholder.png'
import { Dropdown } from 'components/Dropdown'
import ItemWithQuantity from 'components/ItemWithQuantity'
import Loader from 'components/Loader'
import { useGetItemsQuery } from 'containers/items/queries'
import { Link, Outlet, useNavigate, useParams } from 'react-router-dom'
import { Pill } from '../../components/Pill'
import { useGetSingleRecipeQuery } from './queries'

function formatPrepTime(time: number | null) {
    if (!time || !Number.isInteger(time)) {
        return '?'
    }

    if (time < 60) {
        return `${time} mins`
    }

    const mins = time % 60
    const hrs = (time - mins) / 60

    return `${hrs} ${hrs === 1 ? 'hr' : 'hrs'} ${mins} mins`
}

function EditRecipe() {
    const { recipeId } = useParams()
    const navigate = useNavigate()

    const {
        data: getSingleRecipeData,
        isLoading: isGetSingleRecipeLoading,
        isError: isGetSingleRecipeError
    } = useGetSingleRecipeQuery(recipeId || '')
    const { data: getItemsData, isLoading: isGetItemsLoading, isError: isGetItemsError } = useGetItemsQuery()

    if (isGetSingleRecipeLoading || isGetItemsLoading) return <Loader fullPage />
    if (isGetSingleRecipeError || !getSingleRecipeData || isGetItemsError || !getItemsData) return <h1>Recipe error</h1>

    const { name, id, items, method, recipe_category, image_url, prep_time, cook_time, serves } = getSingleRecipeData

    const renderCurrentItems = () => {
        const itemGroupNames = [
            ...new Set(
                items.reduce((acc: string[], curr) => {
                    const group = curr.item_group
                    return group ? [...acc, group] : acc
                }, [])
            )
        ]

        const itemGroups = itemGroupNames.map((groupName) => {
            return {
                name: groupName,
                items: items.filter((item) => item.item_group === groupName)
            }
        })

        const restItems = items.filter((item) => !item.item_group)

        return (
            <>
                <h3>Ingredients</h3>
                <div className='mt-2 overflow-hidden'>
                    <ul className='flex flex-col gap-1'>
                        {[...restItems]
                            .sort((a, b) => (a.name > b.name ? 1 : -1))
                            .map((item) => (
                                <div key={item.recipe_item_id} className='flex items-center gap-2'>
                                    <div className='w-2 h-2 rounded-full bg-secondary-400' />
                                    <ItemWithQuantity
                                        key={item.recipe_item_id}
                                        quantityValue={item.item_quantity.quantity}
                                        unitSymbol={item.item_quantity.quantity_unit?.symbol}
                                        itemName={item.name}
                                    />
                                </div>
                            ))}
                    </ul>
                    {itemGroups.map((group) => {
                        return (
                            <div key={group.name} className='mt-4'>
                                <h5 className='text-[17px] mb-1'>{group.name}</h5>
                                <ul className='pl-4 flex flex-col gap-1'>
                                    {[...group.items]
                                        .sort((a, b) => (a.name > b.name ? 1 : -1))
                                        .map((item) => (
                                            <div key={item.recipe_item_id} className='flex items-center gap-2'>
                                                <div className='w-2 h-2 rounded-full bg-secondary-400' />
                                                <ItemWithQuantity
                                                    key={item.recipe_item_id}
                                                    quantityValue={item.item_quantity.quantity}
                                                    unitSymbol={item.item_quantity.quantity_unit?.symbol}
                                                    itemName={item.name}
                                                />
                                            </div>
                                        ))}
                                </ul>
                            </div>
                        )
                    })}
                </div>
            </>
        )
    }

    return (
        <div className='p-4'>
            <div className='flex justify-between mb-2'>
                <Link to='/recipes'>Back to recipes</Link>
                <div className='flex gap-2'>
                    <button
                        className='h-8 w-8 bg-primary text-white flex justify-center items-center rounded-full'
                        type='button'
                        onClick={() => navigate(`/recipes/edit/${id}/details`)}
                    >
                        <PencilSquareIcon className='w-4' />
                    </button>
                    <Dropdown
                        dropdownClassName='h-8 w-8'
                        menuButtonClassName='!bg-primary !text-white w-full h-full flex justify-center items-center rounded-full'
                        menuButton={<EllipsisHorizontalIcon className='size-6' style={{ transform: 'scale(400%)' }} />}
                        menuItems={[
                            <Dropdown.MenuItem.Button key='1' onClick={() => navigate(`/recipes/edit/${id}/duplicate`)}>
                                <Square2StackIcon className='w-4 text-primary' />
                                Duplicate
                            </Dropdown.MenuItem.Button>,
                            <Dropdown.MenuItem.Button key='2' onClick={() => navigate(`/recipes/edit/${id}/share`)}>
                                <ArrowUpTrayIcon className='w-4 text-primary' />
                                Share
                            </Dropdown.MenuItem.Button>,
                            <Dropdown.MenuItem.Button key='3' onClick={() => navigate(`/recipes/edit/${id}/delete`)}>
                                <TrashIcon className='w-4 text-error' />
                                <p className='text-error'>Delete</p>
                            </Dropdown.MenuItem.Button>
                        ]}
                    />
                </div>
            </div>

            <img className='h-44 w-full sm:w-[500px] object-cover rounded-md' src={image_url || NoImagePlaceholder} alt={name} />

            <div className='mt-2'>
                <h2 className='mb-2'>{name}</h2>
                <Pill>{recipe_category?.name || 'Uncategorized'}</Pill>
                <div className='flex gap-4 mt-4'>
                    <div className='flex gap-1'>
                        <p className='text-secondary-500 text-sm'>Prep:</p>
                        <p className='text-sm'>{formatPrepTime(prep_time)}</p>
                    </div>
                    <div className='flex gap-1'>
                        <p className='text-secondary-500 text-sm'>Cook:</p>
                        <p className='text-sm'>{formatPrepTime(cook_time)}</p>
                    </div>
                    <div className='flex gap-1'>
                        <p className='text-secondary-500 text-sm'>Serves:</p>
                        <p className='text-sm'>{serves}</p>
                    </div>
                </div>
            </div>

            <div className='flex flex-wrap flex-col md:flex-row gap-x-4 gap-y-4 mt-6'>
                <div className='flex-1'>{renderCurrentItems()}</div>
                <div className='flex-1'>
                    <h3 className='mb-2'>Method</h3>
                    <div className='flex flex-col gap-4'>
                        {(() => {
                            if (!method || method.length < 1) {
                                return <p>No method steps. Press the edit button to start adding some steps.</p>
                            }

                            return method.map((step, index) => {
                                return (
                                    <div key={step} className='flex gap-2 p-2 rounded-md border-2 border-primary'>
                                        <p>{(index + 1).toString()}.</p>
                                        <p>{step}</p>
                                    </div>
                                )
                            })
                        })()}
                    </div>
                </div>
            </div>

            <Outlet />
        </div>
    )
}

export default EditRecipe
